import { Icon, Text, Tooltip } from '@wyz/ui';
import { uniq } from 'lodash-es';
import * as React from 'react';
import { Trans } from '~/common/context';
import {
  DISCOUNT_TYPE_TRANSLATIONS,
  VEHICLE_CATEGORY_TRANSLATIONS,
} from '~/common/translators';
import { DistributionPricingPolicyHelpers } from '~/purchase/helpers';
import {
  IDistributionPricingPolicySeason,
  IDistributionPricingPolicySeasonCell,
  IDistributionPricingPolicyVariantAmount,
  IPricingConfigurationType,
} from '~/purchase/types';

const { NO_VALUE, summarizeRange, tooltipClass } =
  DistributionPricingPolicyHelpers;

/** One amount of a divergent cell, placed in the tooltip grid by its diameter and category. */
type VariantDetail = {
  diameter: number;
  category: string;
  value: React.ReactNode;
};

/** An amount and the unit of its discount type, which not every discount type has. */
function Amount({
  value,
  discountType,
}: {
  value: number | string;
  discountType: number | null;
}) {
  const labelKey =
    null === discountType
      ? undefined
      : DISCOUNT_TYPE_TRANSLATIONS[discountType];

  return (
    <>
      {value}
      {labelKey && (
        <>
          {' '}
          <Trans i18nKey={labelKey} />
        </>
      )}
    </>
  );
}

export function PurchaseAmount({
  cell,
  season,
}: {
  cell: IDistributionPricingPolicySeasonCell;
  season: IDistributionPricingPolicySeason;
}) {
  if (0 === cell.variants.length) {
    return (
      <span className='text-center'>
        {null === cell.purchaseValue || null === cell.discountType ? (
          NO_VALUE
        ) : (
          <Amount value={cell.purchaseValue} discountType={cell.discountType} />
        )}
      </span>
    );
  }

  const range = summarizeRange(
    cell.variants.map((variant) => variant.purchaseValue),
    cell.variants.map((variant) => variant.discountType),
  );

  return (
    <VariantTooltip
      season={season}
      details={cell.variants.map((variant) => ({
        diameter: variant.diameter,
        category: variant.category,
        value: (
          <Amount
            value={variant.purchaseValue}
            discountType={variant.discountType}
          />
        ),
      }))}
    >
      <span className='text-center'>
        {null === range ? (
          NO_VALUE
        ) : (
          <Amount value={range.text} discountType={range.discountType} />
        )}
      </span>
    </VariantTooltip>
  );
}

export function TotalAmount({
  cell,
  season,
  bonusType,
  hqBonus,
}: {
  cell: IDistributionPricingPolicySeasonCell;
  season: IDistributionPricingPolicySeason;
  bonusType: IPricingConfigurationType | null;
  hqBonus: number | null;
}) {
  const computeTotal = (purchaseValue: number, discountType: number) => {
    const total = DistributionPricingPolicyHelpers.computeTotal(
      purchaseValue,
      discountType,
      cell.extraBonus,
      bonusType,
      hqBonus,
    );

    return null === total ? null : Number(total.toFixed(2));
  };

  if (0 === cell.variants.length) {
    if (null === cell.purchaseValue || null === cell.discountType) {
      return NO_VALUE;
    }

    const total = computeTotal(cell.purchaseValue, cell.discountType);

    return (
      <Text color='primary' weight='semi-bold'>
        {null === total ? (
          NO_VALUE
        ) : (
          <Amount value={total} discountType={cell.discountType} />
        )}
      </Text>
    );
  }

  const totals = cell.variants
    .map((variant) => ({
      variant,
      total: computeTotal(variant.purchaseValue, variant.discountType),
    }))
    .filter(
      (
        entry,
      ): entry is {
        variant: IDistributionPricingPolicyVariantAmount;
        total: number;
      } => null !== entry.total,
    );

  if (0 === totals.length) {
    return NO_VALUE;
  }

  const range = summarizeRange(
    totals.map((entry) => entry.total),
    totals.map((entry) => entry.variant.discountType),
  );

  return (
    <VariantTooltip
      season={season}
      details={totals.map((entry) => ({
        diameter: entry.variant.diameter,
        category: entry.variant.category,
        value: (
          <Amount
            value={entry.total}
            discountType={entry.variant.discountType}
          />
        ),
      }))}
    >
      <Text component='span' color='primary' weight='semi-bold'>
        {null === range ? (
          NO_VALUE
        ) : (
          <Amount value={range.text} discountType={range.discountType} />
        )}
      </Text>
    </VariantTooltip>
  );
}

/**
 * The combinations behind a divergent cell read as a grid: one column per vehicle category, one row
 * per diameter. A combination the pricing policy does not cover leaves its cell empty.
 */
function VariantTooltip({
  details,
  season,
  children,
}: {
  details: VariantDetail[];
  season: IDistributionPricingPolicySeason;
  children?: React.ReactNode;
}) {
  const categories = uniq(details.map((detail) => detail.category));
  const diameters = uniq(details.map((detail) => detail.diameter)).sort(
    (left, right) => left - right,
  );
  const valueOf = (diameter: number, category: string) =>
    details.find(
      (detail) => detail.diameter === diameter && detail.category === category,
    )?.value;

  return (
    <Tooltip
      className={`extra-bonus-tooltip${tooltipClass(season)}`}
      label={
        <table className='extra-bonus-tooltip__variants'>
          <thead>
            <tr>
              <th />
              {categories.map((category) => (
                <th key={category}>
                  {VEHICLE_CATEGORY_TRANSLATIONS[category] ? (
                    <Trans i18nKey={VEHICLE_CATEGORY_TRANSLATIONS[category]} />
                  ) : (
                    // The catalog category is the label of last resort, when no loco key covers it.
                    category
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {diameters.map((diameter) => (
              <tr key={diameter}>
                <th>
                  <Trans
                    i18nKey='app.distribution.pricing_policy.detail.diameter'
                    values={{ diameter: String(diameter) }}
                  />
                </th>
                {categories.map((category) => (
                  <td key={category}>
                    {valueOf(diameter, category) ?? NO_VALUE}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      }
    >
      <span className='extra-bonus-table__divergent d-flex align-items-center justify-content-center'>
        {children}
        <span className='extra-bonus-table__magnifier'>
          <Icon icon='search' size='xxs' />
        </span>
      </span>
    </Tooltip>
  );
}
